import type { ElevationSample } from './elevation'

export interface PathLossInput {
  samples: ElevationSample[]
  /** Jarak total link dalam km. */
  totalDistance: number
  /** Frekuensi kerja dalam GHz. */
  frequencyGHz: number
  /** Tinggi antena di atas permukaan tanah (meter). */
  antennaHeightNE: number
  antennaHeightFE: number
}

export interface PathLossPoint {
  distance: number
  /** Ketinggian tanah + koreksi lengkung bumi (m). */
  terrain: number
  /** Garis pandang antara kedua antena (m). */
  lineOfSight: number
  /** Batas bawah zona Fresnel pertama (m). */
  fresnelBottom: number
  /** Radius Fresnel pertama di titik ini (m). */
  fresnelRadius: number
  /** Jarak bebas antara tanah dan garis pandang (m). */
  clearance: number
}

export interface PathLossResult {
  points: PathLossPoint[]
  distanceKm: number
  /** Free space path loss (dB). */
  fspl: number
  /** Rugi difraksi knife-edge (dB). */
  diffractionLoss: number
  /** Total estimasi loss (dB). */
  totalLoss: number
  /** Titik paling kritis pada lintasan. */
  worstPoint: PathLossPoint
  /** Persentase zona Fresnel pertama yang bebas di titik kritis. */
  worstFresnelClearance: number
  neElevation: number
  feElevation: number
  status: 'clear' | 'marginal' | 'obstructed'
}

/** Faktor k standar atmosfer untuk koreksi lengkung bumi. */
const K_FACTOR = 1.33

/** Loss difraksi single knife-edge (pendekatan ITU-R P.526). */
function knifeEdgeLoss(v: number): number {
  if (v <= -0.78) return 0
  return 6.9 + 20 * Math.log10(Math.sqrt((v - 0.1) ** 2 + 1) + v - 0.1)
}

export function calculatePathLoss({
  samples,
  totalDistance,
  frequencyGHz,
  antennaHeightNE,
  antennaHeightFE,
}: PathLossInput): PathLossResult {
  const neElevation = samples[0].elevation
  const feElevation = samples[samples.length - 1].elevation

  const antennaTopNE = neElevation + antennaHeightNE
  const antennaTopFE = feElevation + antennaHeightFE

  // Panjang gelombang (meter) — c / f.
  const wavelength = 0.299792458 / frequencyGHz

  const points: PathLossPoint[] = samples.map((sample) => {
    const d1 = sample.distance
    const d2 = Math.max(totalDistance - d1, 0)
    const ratio = totalDistance > 0 ? d1 / totalDistance : 0

    // Bulge lengkung bumi (meter), d dalam km.
    const curvature = (d1 * d2) / (12.75 * K_FACTOR)
    const terrain = sample.elevation + curvature

    const lineOfSight = antennaTopNE + (antennaTopFE - antennaTopNE) * ratio

    const fresnelRadius =
      totalDistance > 0
        ? 17.32 * Math.sqrt((d1 * d2) / (frequencyGHz * totalDistance))
        : 0

    return {
      distance: d1,
      terrain,
      lineOfSight,
      fresnelRadius,
      fresnelBottom: lineOfSight - fresnelRadius,
      clearance: lineOfSight - terrain,
    }
  })

  // Titik kritis = clearance paling kecil relatif terhadap radius Fresnel.
  const interior = points.slice(1, -1)
  const evaluated = interior.length ? interior : points

  const worstPoint = evaluated.reduce((worst, point) => {
    const currentRatio =
      point.fresnelRadius > 0 ? point.clearance / point.fresnelRadius : point.clearance
    const worstRatio =
      worst.fresnelRadius > 0 ? worst.clearance / worst.fresnelRadius : worst.clearance
    return currentRatio < worstRatio ? point : worst
  })

  const worstFresnelClearance =
    worstPoint.fresnelRadius > 0
      ? (worstPoint.clearance / worstPoint.fresnelRadius) * 100
      : 100

  const fspl =
    92.45 + 20 * Math.log10(frequencyGHz) + 20 * Math.log10(Math.max(totalDistance, 0.001))

  // Parameter difraksi Fresnel-Kirchhoff.
  const d1m = Math.max(worstPoint.distance, 0.001) * 1000
  const d2m = Math.max(totalDistance - worstPoint.distance, 0.001) * 1000
  const obstructionHeight = worstPoint.terrain - worstPoint.lineOfSight
  const v =
    obstructionHeight *
    Math.sqrt((2 / wavelength) * (1 / d1m + 1 / d2m))

  const diffractionLoss = knifeEdgeLoss(v)

  const status: PathLossResult['status'] =
    worstFresnelClearance >= 60
      ? 'clear'
      : worstFresnelClearance >= 0
        ? 'marginal'
        : 'obstructed'

  return {
    points,
    distanceKm: totalDistance,
    fspl,
    diffractionLoss,
    totalLoss: fspl + diffractionLoss,
    worstPoint,
    worstFresnelClearance,
    neElevation,
    feElevation,
    status,
  }
}
